import { Observable, Subject, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  Input,
  Component,
  OnDestroy,
  EventEmitter,
  Output,
  AfterViewInit,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { SortDirection } from '@angular/material/sort';

import { ISnListQueryParameters, ISnListResponse, SortParameters } from '@lib/api';
import { SnCamera, SnTag } from '@lib/api/entities';
import { SnCamerasAPI } from '@lib/api/services';
import { FsCameraMarker } from '@lib/cameras/map';
import { FsMap, ICON_ONLY } from '@lib/geo/map';
import { t } from '@lib/translation';
import { SnActionConfig } from 'libs/ui/actions/src/lib/types';
import { SnDataListComponent } from 'libs/ui/data-list/src/lib/components';

import { SnCollection } from '../../../../../common/src/lib/models/collection';
import { CameraDescendantService } from '../../services/camera-descendant.service';
import { CameraTableService } from '../../services/camera-table.service';

@Component({
  selector: 'sn-camera-table-details',
  templateUrl: './camera-table-details.component.html',
  styleUrls: ['./camera-table-details.component.scss'],
})
export class SnCameraTableDetailsComponent implements OnDestroy, AfterViewInit {
  @ViewChild('listElement', { static: true })
  public readonly list: SnDataListComponent<SnCamera>;

  @Input() public readonly canClip;
  @Input() public readonly canAdd;
  @Input() public readonly canEdit;
  @Input() public readonly canShare;
  @Input() public readonly canDelete;

  @Output() public readonly clone = new EventEmitter<SnCamera>();
  @Output() public readonly edit = new EventEmitter<SnCamera>();
  @Output() public readonly clip = new EventEmitter<SnCamera>();
  @Output() public readonly share = new EventEmitter<SnCamera>();
  @Output() public readonly shareInChat = new EventEmitter<SnCamera>();
  @Output() public readonly delete = new EventEmitter<SnCamera>();
  @Output() public readonly add = new EventEmitter<SnCamera>();

  public get columns(): string[] {
    return this._getColumns();
  }

  public readonly actions: SnActionConfig<SnCamera>[] = this._getActions();

  public readonly loadData = this._loadData.bind(this);

  public sort: string = 'name';
  public sortDirection: SortDirection = 'asc';
  public fsMap: FsMap;

  @Input() public camera: SnCamera;
  @Output() public readonly changesInListOfTag = new EventEmitter<SnCollection<SnTag>>();
  @Output() public readonly selectedChildCam = new EventEmitter<SnCamera>();
  public childCameras: SnCamera[] = [];
  public isLoading: boolean = false;
  public selectedCamera: SnCamera;
  public hasUrls: boolean = false;
  public cameraMarker: FsCameraMarker;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _camerasAPI: SnCamerasAPI,
    private readonly _descendantsService: CameraDescendantService,
    private readonly _cd: ChangeDetectorRef,
    private readonly _cameraTableService: CameraTableService,
  ) {
    this.selectedCamera = this.camera;
  }

  public ngAfterViewInit(): void {
    this._initSubscriptions();
  }

  public onMapInit(fsMap: FsMap): void {
    this.fsMap = fsMap;
    this.cameraMarker = new FsCameraMarker({
      map: fsMap,
      camera: this.camera,
      initialStyle: ICON_ONLY,
    });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onTagsChanged(tags: SnTag[]): void {
    this._camerasAPI
      .updateCamera(this.camera.id, { tags })
      .pipe(takeUntil(this._destroy$))
      .subscribe((targetCamera: SnCamera) => {
        this.camera = targetCamera;
        this.changesInListOfTag.emit(this.camera.tags);
      });
  }

  public onSelectedCam(camera: SnCamera): void {
    this.selectedCamera = camera;
  }

  public getChildCameras(): void {
    this.isLoading = true;
    this._camerasAPI
      .getDescendantList(this.camera.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.childCameras = response.results;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  public goToParent(): void {
    this.selectedCamera = this.camera;
  }

  public reloadData(): void {
    this.list.reference.reload();
  }

  private _loadData(params: ISnListQueryParameters): Observable<ISnListResponse<SnCamera>> {
    return this._camerasAPI.getDescendantList(this.camera.id, this._prepareQueryParams(params));
  }

  private _prepareQueryParams<T extends ISnListQueryParameters>(params: T): T {
    return {
      ...params,
      ordering: new SortParameters({
        active: this.sort,
        direction: this.sortDirection,
      }).convertedActive,
      pageSize: 5,
      search: '',
    };
  }

  private _getActions(): SnActionConfig<SnCamera>[] {
    return [
      {
        label: t('common.options'),
        icon: {
          fontSet: 'fal',
          fontIcon: 'fa-ellipsis-vertical',
        },
        menu: {
          items: [
            {
              label: t('cameras.details.actions.removeFromParent'),
              icon: {
                fontSet: 'fal',
                fontIcon: 'fa-link-slash',
              },
              click: (camera) => this._removeParent(camera),
              hidden: (camera) => !this.canEdit || !camera.parentCamera,
            },
            {
              label: t('common.createClip'),
              icon: {
                fontSet: 'fal',
                fontIcon: 'fa-film',
              },
              click: (camera) => this.clip.next(camera),

              hidden: (camera) => {
                return !this.canClip;
              },
            },
            {
              label: t('common.edit'),
              icon: {
                fontSet: 'fal',
                fontIcon: 'fa-pen',
              },
              hidden: () => !this.canEdit,
              click: (camera) => {
                this.edit.next(camera);
              },
            },
            {
              label: t('common.clone'),
              icon: {
                fontSet: 'fal',
                fontIcon: 'fa-clone',
              },
              hidden: () => !this.canAdd,
              click: (camera) => this.clone.next(camera),
            },
            {
              label: t('common.share'),
              icon: {
                fontSet: 'fal',
                fontIcon: 'fa-share-alt',
              },
              hidden: () => !this.canShare,
              click: (camera) => this.share.next(camera),
            },
            {
              label: t('common.shareInChat'),
              icon: {
                fontSet: 'fal',
                fontIcon: 'fa-reply',
              },
              hidden: () => !this.canShare,
              click: (camera) => this.shareInChat.next(camera),
            },
            {
              label: t('common.delete'),
              icon: {
                fontSet: 'fal',
                fontIcon: 'fa-trash-alt',
              },
              hidden: () => !this.canDelete,
              click: (camera) => this.delete.next(camera),
            },
          ],
        },
      },
    ];
  }

  private _getColumns(): string[] {
    return [
      'name',
      'make',
      'model',
      'disposition',
      'location',
      'floor',
      'record',
      'status',
      'isAiCamera',
      'actions',
    ];
  }

  private _initSubscriptions(): void {
    this.selectedCamera = this.camera;
    this._camerasAPI
      .getCamera(this.selectedCamera.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => {
        const cam = res;
        this.hasUrls = true;
        const newCameraRequest = new SnCamera({
          ...this.camera,
          ...cam,
        });
        this.camera = newCameraRequest;
        this.selectedCamera = this.camera;
        this._cd.detectChanges();
      });

    this._descendantsService.created$.pipe(takeUntil(this._destroy$)).subscribe((camera) => {
      if (camera.parentCamera === this.camera.id) {
        this.list.dataSource.insertItemToStart(camera);
      }
    });

    this._descendantsService.updated$.pipe(takeUntil(this._destroy$)).subscribe((camera) => {
      if (camera.parentCamera === this.camera.id) {
        this.list.dataSource.updateItem(camera);
      }
    });
    this._descendantsService.deleted$.pipe(takeUntil(this._destroy$)).subscribe((camera) => {
      if (camera.parentCamera === this.camera.id) {
        this.list.dataSource.deleteItem(camera);
      }
    });

    if (this.camera.make?.toLowerCase()?.includes('skydio')) {
      this._cameraTableService.cameraUpdated$
        .pipe(takeUntil(this._destroy$))
        .subscribe((camera) => {
          if (this.cameraMarker?.camera.id === camera?.id) {
            this.cameraMarker.setDestination({
              lat: camera.geometry.coordinates[1],
              lon: camera.geometry.coordinates[0],
            });
            this.cameraMarker.setDeltas(3);
          }
        });
      interval(100)
        .pipe(takeUntil(this._destroy$))
        .subscribe(() => {
          if (this.cameraMarker && this.cameraMarker.destination) {
            this.cameraMarker.move();
          }
        });
    }
  }

  private _removeParent(camera: SnCamera): void {
    this._camerasAPI
      .unsetParent(camera)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.list.dataSource.deleteItem(camera);
      });
  }
}